<script setup lang="ts">
import {useCartStore} from "../cart.store";
import {storeToRefs} from "pinia";
import {ref, watch} from "vue";
import OptionMiniBanner from "./OptionMiniBanner.vue";
import {useWebPopup} from "lct-lib";

const {show} = useWebPopup();

const {removeItem, saveItemInCart, applyPromoByCode} = useCartStore();
const {cart, isLoaded, subtotal, tax, total, shipCost, discount} = storeToRefs(useCartStore());

const promoCode = ref("");

const getReferenceAssets = (value: any): string[] => {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((url) => url.trim())
    .filter((url) => !!url && /^https?:\/\//i.test(url));
};

const toCheckoutPage = () => {
  location.href = "/cart/checkout";
};

const applyPromoCode = async () => {
  if (!promoCode.value) return;
  try {
    const code = promoCode.value.trim().toUpperCase();
    const promo = await applyPromoByCode(code);
    if (promo) {
      show(`Promo code ${code} applied successfully!`);
    } else {
      show(`Promo code ${code} is not valid.`);
    }
  } catch (e) {
    console.error(e);
    show("An error occurred while applying promo code.");
  }
};

watch(
  () => cart.value.promo,
  () => {
    promoCode.value = cart.value.promo?.code || "";
  },
  {immediate: true}
);
</script>

<template>
  <div c2c-0 class="cart-page">
    <div c2c-1 class="cart-loading" v-if="!isLoaded">
      <div c2c-2 class="loading-head sk"></div>
      <div c2c-3 class="loading-layout">
        <div c2c-4 class="loading-items">
          <div c2c-5 class="loading-item sk" v-for="n in 2" :key="n"></div>
        </div>
        <div c2c-6 class="loading-summary sk"></div>
      </div>
    </div>

    <div c2c-7 class="cart-wrap" v-else-if="cart && cart.items && cart.items.length">
      <header c2c-8 class="cart-head">
        <h1>Cart</h1>
        <p>{{ cart.items.length }} line item(s)</p>
      </header>

      <div c2c-9 class="cart-layout">
        <section c2c-10 class="items-col">
          <article c2c-11 class="item-card" v-for="(item, index) in cart.items" :key="index">
            <div c2c-12 class="item-top">
              <div c2c-13 class="item-preview">
                <div c2c-14 class="thumb-box" v-if="item.item_type === 'tool_design'" v-html="item.svg"></div>
                <div c2c-15 class="thumb-box" v-if="item.item_type === 'order_form'">
                  <img :src="item.img_url || item.jpg_url" alt="banner">
                </div>
              </div>

              <div c2c-16 class="item-main">
                <div c2c-17 class="item-head-row">
                  <p c2c-18 class="item-price">
                    <span c2c-19 class="sale">${{ item.sale_price }}</span>
                    <span c2c-20 class="origin">${{ item.price }}</span>
                  </p>
                  <div c2c-21 class="qty-box">
                    <label>Qty</label>
                    <input type="number" min="1" v-model="item.qty" @change="saveItemInCart">
                  </div>
                </div>

                <div c2c-22 class="item-info" v-if="item.item_type === 'tool_design'">
                  <span c2c-23 class="name">{{ item.name_model }} - </span>
                  <span>{{ item.information }}</span>
                </div>

                <div c2c-24 class="item-info" v-if="item.item_type === 'order_form'">
                  <p c2c-25 class="desc" v-html="item.information"></p>
                  <div c2c-26 class="meta-wrap">
                    <div c2c-27 class="meta-row" v-for="(value, key) in item.form_data" :key="String(key)">
                      <template v-if="key && <any>key === 'players'">
                        <span c2c-28 class="meta-key">Players:</span>
                        <span c2c-29 class="meta-val">
                          <template v-for="(p, playerIndex) in item.form_data.players" :key="playerIndex">
                            <span v-if="p.name">{{ p.name }}({{ p.number }})</span>
                            <span v-if="playerIndex < item.form_data.players.length - 1">. </span>
                          </template>
                        </span>
                      </template>
                      <template v-else-if="key && <any>key === 'reference assets'">
                        <span c2c-30 class="meta-key">Reference assets:</span>
                        <span c2c-31 class="meta-val">
                          <div c2c-32 class="asset-preview-grid" v-if="getReferenceAssets(value).length">
                            <a
                              v-for="assetUrl in getReferenceAssets(value)"
                              :key="assetUrl"
                              c2c-33 class="asset-preview-item"
                              :href="assetUrl"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img :src="assetUrl" alt="Reference asset">
                            </a>
                          </div>
                        </span>
                      </template>
                      <template v-else>
                        <span c2c-34 class="meta-key">{{ key }}:</span>
                        <span c2c-35 class="meta-val">{{ value }}</span>
                      </template>
                    </div>
                  </div>
                </div>

                <div c2c-36 class="item-actions">
                  <a v-if="item.item_type === 'tool_design'" :href="`/design-tool?c=${index}`">
                    <span c2c-37 class="icon-feather"></span>
                    Edit design
                  </a>
                  <a v-if="item.item_type === 'order_form'" :href="`/design-tool?u=${item.svg_url}`">
                    <span c2c-38 class="icon-feather"></span>
                    Edit design
                  </a>
                  <button type="button" c2c-39 class="remove-btn" @click="removeItem(index)">
                    <span c2c-40 class="icon-trash-empty"></span>
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <div c2c-41 class="item-extra" v-if="item.type === 'hem-grommet' || item.type === 'pole-pocket'">
              <option-mini-banner v-model="item.option"></option-mini-banner>
            </div>

            <div c2c-42 class="item-extra">
              <label c2c-43 class="proof-row">
                <input type="checkbox" v-model="item.is_proof" @change="saveItemInCart">
                <span>Email Proof</span>
              </label>
              <p c2c-44 class="proof-note">
                We will send you a design proof before printing. The design will not be printed until you approve it.
              </p>
            </div>
          </article>
        </section>

        <aside c2c-45 class="summary-col">
          <div c2c-46 class="summary-card">
            <h2>Order Summary</h2>

            <div c2c-47 class="promo-row">
              <input type="text" placeholder="Promo code" v-model="promoCode">
              <button type="button" @click="applyPromoCode">Apply</button>
            </div>

            <table c2c-48 class="summary-table">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td>${{ subtotal }}</td>
                </tr>
                <tr>
                  <td>Ship cost</td>
                  <td>${{ shipCost }}</td>
                </tr>
                <tr>
                  <td>Discount <span v-if="cart.promo?.code">({{ cart.promo.code }})</span></td>
                  <td c2c-49 class="discount">-${{ discount }}</td>
                </tr>
                <tr v-if="cart.promo?.name">
                  <td colspan="2" c2c-50 class="promo-name">({{ cart.promo.name }})</td>
                </tr>
                <tr>
                  <td>Tax</td>
                  <td>${{ tax }}</td>
                </tr>
                <tr c2c-51 class="total-row">
                  <td>Total</td>
                  <td>${{ total }}</td>
                </tr>
              </tbody>
            </table>

            <button type="button" c2c-52 class="checkout-btn" @click="toCheckoutPage">Proceed To Checkout</button>
          </div>
        </aside>
      </div>
    </div>

    <div c2c-53 class="empty-box" v-else>
      <p>The cart is empty</p>
    </div>
  </div>
</template>

<style scoped>
.cart-page {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 14px 44px;
}

.cart-loading {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.loading-head {
  height: 48px;
  width: min(280px, 100%);
  border-radius: 10px;
}

.loading-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 18px;
}

.loading-items {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.loading-item {
  height: 240px;
  border-radius: 14px;
}

.loading-summary {
  height: 340px;
  border-radius: 14px;
}

.sk {
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 220% 100%;
  animation: sk 1.25s ease-in-out infinite;
}

@keyframes sk {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.cart-head {
  margin-bottom: 14px;
}

.cart-head h1 {
  margin: 0;
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.1;
  color: #0f172a;
}

.cart-head p {
  margin: 6px 0 0;
  color: #64748b;
}

.cart-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 18px;
}

.item-card,
.summary-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
}

.item-card {
  padding: 14px;
}

.item-card + .item-card {
  margin-top: 14px;
}

.item-top {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 14px;
}

.thumb-box {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  background: #f8fafc;
}

.thumb-box img {
  width: 100%;
  display: block;
  border-radius: 8px;
}

.item-head-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.item-price {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.sale {
  color: #dc2626;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.origin {
  color: #64748b;
  text-decoration: line-through;
  font-weight: 700;
}

.qty-box {
  text-align: right;
}

.qty-box label {
  display: block;
  font-size: 12px;
  color: #64748b;
}

.qty-box input {
  width: 78px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  text-align: center;
}

.item-info {
  margin-top: 10px;
  color: #334155;
}

.item-info .name,
.meta-key {
  font-weight: 700;
}

.item-info .desc {
  margin: 0;
}

.meta-wrap {
  margin-top: 8px;
}

.meta-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 14px;
  margin-top: 4px;
}

.asset-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(86px, 1fr));
  gap: 8px;
  width: min(420px, 100%);
}

.asset-preview-item {
  display: block;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.asset-preview-item img {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

.item-actions {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.item-actions a {
  color: #0b5ad9;
  text-decoration: none;
  font-weight: 700;
}

.remove-btn {
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #dc2626;
  border-radius: 8px;
  min-height: 34px;
  padding: 0 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.item-extra {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #cbd5e1;
}

.proof-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}

.proof-note {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.summary-card {
  padding: 16px;
  position: sticky;
  top: 14px;
  background:
    radial-gradient(140% 160% at 100% 0%, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 58%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.summary-card h2 {
  margin: 0;
  font-size: 24px;
  color: #0f172a;
}

.promo-row {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr auto;
}

.promo-row input {
  height: 40px;
  border: 1px solid #cbd5e1;
  border-right: none;
  border-radius: 9px 0 0 9px;
  padding: 0 10px;
}

.promo-row button {
  height: 40px;
  padding: 0 14px;
  border: 1px solid #0b5ad9;
  border-radius: 0 9px 9px 0;
  background: #0b5ad9;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
}

.summary-table {
  width: 100%;
  margin-top: 14px;
  border-collapse: collapse;
}

.summary-table td {
  padding: 8px 0;
  font-weight: 700;
  color: #334155;
}

.summary-table td:last-child {
  text-align: right;
}

.summary-table .discount {
  color: #dc2626;
}

.promo-name {
  color: #dc2626;
  font-style: italic;
  font-weight: 600;
}

.total-row td {
  border-top: 1px solid #dbeafe;
  padding-top: 12px;
  color: #0f172a;
  font-size: 19px;
}

.checkout-btn {
  margin-top: 14px;
  width: 100%;
  min-height: 44px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(90deg, #0b5ad9 0%, #2563eb 100%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.empty-box {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-box p {
  font-size: 34px;
  font-weight: 800;
  color: #0f172a;
}

@media (max-width: 1020px) {
  .loading-layout {
    grid-template-columns: 1fr;
  }

  .cart-layout {
    grid-template-columns: 1fr;
  }

  .summary-card {
    position: static;
  }
}

@media (max-width: 700px) {
  .loading-item {
    height: 190px;
  }

  .item-top {
    grid-template-columns: 1fr;
  }

  .item-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 560px) {
  .cart-page {
    padding: 16px 10px 36px;
  }

  .sale {
    font-size: 24px;
  }
}
</style>
<style lang="scss" scoped></style>